import { HeatingCalculationResults } from './heating'
import { CoolingCalculationResults } from './cooling'
import { CirculationCalculationResults } from './circulation'
import {
  getCoolingBasedOnHeating,
  getEer,
  getElectricityBasedOnCooling,
  getElectricityBasedOnHeating,
  HeatingHeatPumpResult,
} from './heat-pump-util'
import { HeatingOrCoolingSource } from '../../../generated/graphql'
import { reduceToSum } from '../../../util/array-utility'
import {
  sourceToHeatSourceType,
  HeatSourceType,
  calculateHeatCollectionTemperature,
} from './heating-util'
import { PreCalculationResults } from './pre-calculation'

export type ExtraHeatingResults = {
  recycledHeatingBasic: number
  recycledHeatingDhw: number
  heatingBasic: number
  heatingDhw: number
  totalHeating: number
}

export type ExtraHeatingCops = {
  recycledHeatingCopBasic: number
  recycledHeatingCopDhw: number
  heatingCopBasic: number
  heatingCopDhw: number
  totalHeatingCop: number
}

export type ExtraCoolingResults = {
  totalCooling: number
  recycledCooling: number
  freeCooling: number
  activeCooling: number
}

export type ExtraCoolingEers = {
  totalCoolingEer: number
  freeCoolingEer: number
  activeCoolingEer: number
}

export type ExtraElectricityResults = {
  electricityRequiredByTotal: number
  electricityRequiredByHeating: number
  electricityRequiredByCooling: number
  electricityRequiredByRecyclingHeatingBasic: number
  electricityRequiredByRecyclingHeatingDhw: number
  electricityRequiredByHeatingBasic: number
  electricityRequiredByHeatingDhw: number
  electricityRequiredByFreeCooling: number
  electricityRequiredByActiveCooling: number
}

export type ExtraSourceUsageResults = {
  totalSourceHeating: number
  groundSourceHeating: number
  airSourceHeating: number
  wasteSourceHeating: number
  energyBusSourceHeating: number
  totalSourceCooling: number
  groundSourceCooling: number
  airSourceCooling: number
  energyBusSourceCooling: number
}

export type ExtraTemperatureResults = {
  temperatureHeatingBasic: number
  temperatureHeatingDhw: number
  temperatureCooling: number
  heatCollectionTemperature: number
}

export function calculateExtraHeatingResults(
  circulationCalculationResults: CirculationCalculationResults,
  heatingCalculationResults: HeatingCalculationResults,
): ExtraHeatingResults {
  const recycledHeatingBasic =
    circulationCalculationResults.heating.basic.providedHeating
  const recycledHeatingDhw =
    circulationCalculationResults.heating.dhw.providedHeating
  const heatingBasic = heatingCalculationResults.basic.providedHeating
  const heatingDhw = heatingCalculationResults.dhw.providedHeating
  const totalHeating =
    recycledHeatingBasic + recycledHeatingDhw + heatingBasic + heatingDhw

  return {
    recycledHeatingBasic,
    recycledHeatingDhw,
    heatingBasic,
    heatingDhw,
    totalHeating,
  }
}

export function calculateExtraHeatingCops(
  circulationCalculationResults: CirculationCalculationResults,
  heatingCalculationResults: HeatingCalculationResults,
  totalHeating: number,
): ExtraHeatingCops {
  const getWeightedCop = ({ cop, providedHeating }: HeatingHeatPumpResult) =>
    Number.isNaN(cop) ? 0 : cop * providedHeating

  const recycledHeatingCopBasic =
    circulationCalculationResults.heating.basic.cop
  const recycledHeatingCopDhw = circulationCalculationResults.heating.dhw.cop
  const heatingCopBasic = heatingCalculationResults.basic.cop
  const heatingCopDhw = heatingCalculationResults.dhw.cop
  const totalHeatingCop =
    (getWeightedCop(circulationCalculationResults.heating.basic) +
      getWeightedCop(circulationCalculationResults.heating.dhw) +
      getWeightedCop(heatingCalculationResults.basic) +
      getWeightedCop(heatingCalculationResults.dhw)) /
    totalHeating

  return {
    recycledHeatingCopBasic,
    recycledHeatingCopDhw,
    heatingCopBasic,
    heatingCopDhw,
    totalHeatingCop,
  }
}

export function calculateExtraCoolingResults(
  circulationCalculationResults: CirculationCalculationResults,
  coolingCalculationResults: CoolingCalculationResults,
): ExtraCoolingResults {
  const recycledCooling =
    getCoolingBasedOnHeating(circulationCalculationResults.heating.basic) +
    getCoolingBasedOnHeating(circulationCalculationResults.heating.dhw)
  const freeCooling = coolingCalculationResults.freeCooling.providedCooling
  const activeCooling = coolingCalculationResults.active.providedCooling
  const totalCooling = recycledCooling + freeCooling + activeCooling

  return {
    totalCooling,
    recycledCooling,
    freeCooling,
    activeCooling,
  }
}

export function calculateExtraCoolingEers(
  coolingCalculationResults: CoolingCalculationResults,
  totalCooling: number,
): ExtraCoolingEers {
  const freeCoolingEer = coolingCalculationResults.freeCooling.eer
  const activeCoolingEer = coolingCalculationResults.active.eer
  // Cooling produced by heat recycling is not counted as part of the total cooling electricity consumption
  const totalCoolingElectricity =
    getElectricityBasedOnCooling(coolingCalculationResults.freeCooling) +
    getElectricityBasedOnCooling(coolingCalculationResults.active)
  const totalCoolingEer = getEer(totalCooling, totalCoolingElectricity)

  return {
    totalCoolingEer,
    freeCoolingEer,
    activeCoolingEer,
  }
}

export function calculateExtraElectricityResults(
  circulationCalculationResults: CirculationCalculationResults,
  heatingCalculationResults: HeatingCalculationResults,
  coolingCalculationResults: CoolingCalculationResults,
): ExtraElectricityResults {
  const electricityRequiredByRecyclingHeatingBasic =
    getElectricityBasedOnHeating(circulationCalculationResults.heating.basic)
  const electricityRequiredByRecyclingHeatingDhw = getElectricityBasedOnHeating(
    circulationCalculationResults.heating.dhw,
  )
  const electricityRequiredByHeatingBasic = getElectricityBasedOnHeating(
    heatingCalculationResults.basic,
  )
  const electricityRequiredByHeatingDhw = getElectricityBasedOnHeating(
    heatingCalculationResults.dhw,
  )
  const electricityRequiredByFreeCooling = getElectricityBasedOnCooling(
    coolingCalculationResults.freeCooling,
  )
  const electricityRequiredByActiveCooling = getElectricityBasedOnCooling(
    coolingCalculationResults.active,
  )

  const electricityRequiredByHeating =
    electricityRequiredByRecyclingHeatingBasic +
    electricityRequiredByRecyclingHeatingDhw +
    electricityRequiredByHeatingBasic +
    electricityRequiredByHeatingDhw
  const electricityRequiredByCooling =
    electricityRequiredByFreeCooling + electricityRequiredByActiveCooling
  const electricityRequiredByTotal =
    electricityRequiredByHeating + electricityRequiredByCooling

  return {
    electricityRequiredByTotal,
    electricityRequiredByHeating,
    electricityRequiredByCooling,
    electricityRequiredByRecyclingHeatingBasic,
    electricityRequiredByRecyclingHeatingDhw,
    electricityRequiredByHeatingBasic,
    electricityRequiredByHeatingDhw,
    electricityRequiredByFreeCooling,
    electricityRequiredByActiveCooling,
  }
}

export function calculateSourceUsages(
  heatingCalculationResults: HeatingCalculationResults,
  coolingCalculationResults: CoolingCalculationResults,
): ExtraSourceUsageResults {
  const calculateSourceUsage = (sourceType: HeatingOrCoolingSource) => {
    const heatCollected = heatingCalculationResults.heatSources
      .filter((source) => source.type === sourceToHeatSourceType(sourceType))
      .map((source) => source.collectedHeat ?? 0)
      .reduce(reduceToSum, 0)

    const freeCooling =
      coolingCalculationResults.freeCooling.heatToSource.type === sourceType
        ? coolingCalculationResults.freeCooling.heatToSource.amount
        : 0

    const activeCooling =
      coolingCalculationResults.active.heatToSource.type === sourceType
        ? coolingCalculationResults.active.heatToSource.amount
        : 0

    return { heating: heatCollected, cooling: freeCooling + activeCooling }
  }

  const groundSourceUsage = calculateSourceUsage(HeatingOrCoolingSource.Ground)
  const airSourceUsage = calculateSourceUsage(HeatingOrCoolingSource.Air)
  const energyBusSourceUsage = calculateSourceUsage(
    HeatingOrCoolingSource.EnergyBus,
  )

  const wasteSourceHeating = heatingCalculationResults.heatSources
    .filter(
      (heatSource) =>
        heatSource.type === HeatSourceType.Waste && heatSource.collectedHeat,
    )
    .map((heatSource) => heatSource.collectedHeat ?? 0)
    .reduce(reduceToSum, 0)

  const totalSourceHeating =
    groundSourceUsage.heating +
    airSourceUsage.heating +
    energyBusSourceUsage.heating +
    wasteSourceHeating

  const totalSourceCooling =
    groundSourceUsage.cooling +
    airSourceUsage.cooling +
    energyBusSourceUsage.cooling

  return {
    groundSourceHeating: groundSourceUsage.heating,
    groundSourceCooling: groundSourceUsage.cooling,
    airSourceHeating: airSourceUsage.heating,
    airSourceCooling: airSourceUsage.cooling,
    energyBusSourceHeating: energyBusSourceUsage.heating,
    energyBusSourceCooling: energyBusSourceUsage.cooling,
    wasteSourceHeating,

    totalSourceHeating,
    totalSourceCooling,
  }
}

export function calculateExtraTemperatureResults(
  preCalculationResults: PreCalculationResults,
  heatingCalculationResults: HeatingCalculationResults,
): ExtraTemperatureResults {
  const heatCollectionTemperature = calculateHeatCollectionTemperature(
    heatingCalculationResults.heatSources,
  )

  return {
    temperatureHeatingBasic:
      preCalculationResults.heating?.basic.temperature ?? NaN,
    temperatureHeatingDhw:
      preCalculationResults.heating?.dhw.temperature ?? NaN,
    temperatureCooling: preCalculationResults.cooling?.temperature ?? NaN,
    heatCollectionTemperature,
  }
}
